// Shared pricing helpers — keeps Pages, Pricing, Preview, and Customer View math identical.
//
// Tides Hub supports TWO profit-math modes on a per-quote basis:
//
//   mode = "margin" (default, industry-standard for contractors)
//     price = cost / (1 - profitPct/100)
//     30% margin on $15,448 cost = $22,068  (30% of $22,068 is profit)
//
//   mode = "markup" (simple "add on top of cost")
//     price = cost * (1 + profitPct/100)
//     30% markup on $15,448 cost = $20,082  (added $4,634 on top)
//
// Vendor tax is applied to the raw cost. Final = priceWithProfit + vendorTaxOnRaw.
// The manual "Sells For" (base_price) override skips both modes entirely and is
// used verbatim, so nothing about the override behavior changes.

// Computes customer-facing price from raw cost using the selected profit mode.
export const applyProfit = (rawCost, profitPct, mode = "margin") => {
  const raw = parseFloat(rawCost) || 0;
  const p = parseFloat(profitPct) || 0;
  if (p <= 0) return raw;
  if (mode === "markup") {
    return raw * (1 + p / 100);
  }
  // margin (default). Clamp at 99.9% so we never divide by zero.
  const clamped = Math.min(Math.max(p, 0), 99.9);
  return raw / (1 - clamped / 100);
};

// Legacy alias kept so existing imports keep working. Applies MARGIN math.
export const applyMargin = (rawCost, profitPct) => applyProfit(rawCost, profitPct, "margin");

// Per line-item raw cost: qty * (1 + waste%) * unit_price * (1 + per-item margin%)
// Note: per-item "margin" here is a per-item markup multiplier, kept as-is for backward compat.
export const calcItemRaw = (it) => {
  const qty = parseFloat(it.quantity) || 0;
  const price = parseFloat(it.unit_price) || 0;
  const margin = parseFloat(it.margin) || 0;
  const waste = parseFloat(it.waste_factor) || 0;
  const effectiveQty = qty * (1 + waste / 100);
  return effectiveQty * price * (1 + margin / 100);
};

// Computes the full customer-facing price for an option page.
// rawOption = sum of option's own line items
// rawShared = sum of structure-page line items (shared across options)
// profitPct, taxPct, optMkpPct, optAdj come from the quote/page settings.
// mode: "margin" (default) or "markup"
export const calcOptionPrice = ({
  rawOption = 0,
  rawShared = 0,
  manualBase = 0,
  profitPct = 0,
  taxPct = 0,
  optMkpPct = 0,
  optAdj = 0,
  mode = "margin",
}) => {
  const totalRaw = rawOption + rawShared;
  if (manualBase > 0) {
    // Manual sells-for is authoritative — only stack per-option markup/adjust on top.
    return manualBase + (manualBase * optMkpPct / 100) + optAdj;
  }
  const withProfit = applyProfit(totalRaw, profitPct, mode);
  const tax = totalRaw * (taxPct / 100);
  const base = withProfit + tax;
  return base + (base * optMkpPct / 100) + optAdj;
};
